// Renderer, the calibrated perspective camera, lights, resize and the frame
// rate counter. The backdrop is rendered first into the same canvas, then the
// depth buffer is cleared and the 3D scene is drawn on top.
import * as THREE from 'three';
import { config } from '../config.js';

export function createScene(container, { log = () => {} } = {}) {
  const renderer = new THREE.WebGLRenderer({
    antialias: true,
    alpha: false,
    powerPreference: 'high-performance',
  });
  renderer.domElement.className = 'mw-canvas';
  renderer.autoClear = false;
  renderer.setClearColor(0x000000, 1);
  container.append(renderer.domElement);

  let pixelRatio = Math.min(window.devicePixelRatio || 1, config.render.maxPixelRatio);
  renderer.setPixelRatio(pixelRatio);

  const scene = new THREE.Scene();
  // fov is calibrated in Phase 4; near/far are metres.
  const camera = new THREE.PerspectiveCamera(67, 1, 0.01, 10);

  // Placeholder lighting until Phase 9 drives it from the video.
  const hemi = new THREE.HemisphereLight(0xffffff, 0x556655, 1.0);
  const key = new THREE.DirectionalLight(0xffffff, 1.5);
  key.position.set(0.3, 1.0, 0.5);
  scene.add(hemi, key);

  const stats = { fps: 0, width: 0, height: 0, pixelRatio };
  const resizeListeners = new Set();

  function resize() {
    const w = window.innerWidth;
    const h = window.innerHeight;
    renderer.setSize(w, h, true);
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    stats.width = w;
    stats.height = h;
    for (const fn of resizeListeners) fn(w, h);
  }
  window.addEventListener('resize', resize);
  resize();

  // Rolling one-second frame rate, and a pixel-ratio drop if it stays low.
  let frames = 0;
  let windowStart = performance.now();
  let lowSince = null;
  function countFrame(now) {
    frames++;
    if (now - windowStart < 1000) return;
    stats.fps = (frames * 1000) / (now - windowStart);
    frames = 0;
    windowStart = now;
    if (stats.fps >= config.render.fpsDropThreshold) {
      lowSince = null;
      return;
    }
    lowSince ??= now;
    if (now - lowSince >= config.render.fpsDropSeconds * 1000 && pixelRatio > config.render.reducedPixelRatio) {
      pixelRatio = config.render.reducedPixelRatio;
      stats.pixelRatio = pixelRatio;
      renderer.setPixelRatio(pixelRatio);
      resize();
      log(`fps ${stats.fps.toFixed(0)} for ${config.render.fpsDropSeconds}s, pixel ratio -> ${pixelRatio}`);
    }
  }

  function render(backdrop, now) {
    countFrame(now);
    renderer.clear();
    backdrop?.render(renderer);
    renderer.clearDepth();
    renderer.render(scene, camera);
  }

  return {
    renderer,
    scene,
    camera,
    lights: { hemi, key },
    stats,
    render,
    onResize: (fn) => {
      resizeListeners.add(fn);
      fn(stats.width, stats.height);
    },
  };
}
